import type * as SheetJS from "xlsx";
import { parseStatementDate } from "./statement-date.ts";
import { ftHeader } from "./ft-number.ts";

/** Decode Excel serials as calendar components, not JS instants. cellNF keeps
 * date number formats; cellDates:false avoids timezone-dependent Date objects.
 * Other values, worksheet coordinates and the source workbook are preserved.
 */
export function statementSpreadsheetGrid(workbook: SheetJS.WorkBook, xlsx: typeof SheetJS): unknown[][] {
  const source = workbook.Sheets[workbook.SheetNames[0]];
  if (!source) return [];
  const sheet = { ...source };
  const date1904 = workbook.Workbook?.WBProps?.date1904 === true;
  for (const address of Object.keys(source)) {
    if (address.startsWith("!")) continue;
    const cell = source[address] as SheetJS.CellObject | undefined;
    if (cell?.t !== "n" || typeof cell.v !== "number" || cell.z === undefined) continue;
    const code = typeof cell.z === "number" ? xlsx.SSF.get_table()[cell.z] : cell.z;
    if (typeof code !== "string" || !xlsx.SSF.is_date(code)) continue;
    const p = xlsx.SSF.parse_date_code(cell.v, { date1904 });
    if (!p) continue;
    const pad = (n: number, width = 2) => String(n).padStart(width, "0");
    const time = `${pad(p.H)}:${pad(p.M)}:${pad(p.S)}`;
    const format = code.replace(/"[^"]*"|\\.|_.|\*./g, "");
    const timeOnly = !/[yd]/i.test(format) && /[hs]/i.test(format);
    const date = `${pad(p.y, 4)}-${pad(p.m)}-${pad(p.d)}`;
    // Excel's imaginary 1900-02-29 must remain invalid for Owner review.
    const value = timeOnly ? time : parseStatementDate(date) ? (/[hs]/i.test(format) ? `${date} ${time}` : date) : date;
    sheet[address] = { ...cell, t: "s", v: value, w: value };
  }
  const grid = xlsx.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "", raw: true });
  const header = grid.findIndex(row => row.some(value => typeof value === "string" && ftHeader(value))
    && row.some(value => /^(?:date|payment date|transaction date|tanggal|tanggal transaksi|amount|nominal|debet|kredit)$/i.test(String(value).trim())));
  if (header >= 0) {
    const range = xlsx.utils.decode_range(sheet["!ref"] || "A1");
    const columns = grid[header].flatMap((value, i) => typeof value === "string" && ftHeader(value) ? [i] : []);
    for (let r = header + 1; r < grid.length; r++) for (const c of columns) {
      const cell = source[xlsx.utils.encode_cell({ r: range.s.r + r, c: range.s.c + c })] as SheetJS.CellObject | undefined;
      if (cell?.t !== "n") continue;
      // A rounded Excel number is not reliable identifier evidence.
      if (typeof cell.v !== "number" || !Number.isSafeInteger(cell.v) || cell.v < 0) { grid[r][c] = ""; continue; }
      grid[r][c] = typeof cell.z === "string" && /^0+$/.test(cell.z.trim())
        ? xlsx.utils.format_cell({ ...cell }) : String(cell.v);
    }
  }
  return grid;
}
